import React from 'react';
import { useHistory } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import Icon, { type IconKey } from 'flow/components/ui-components/Icon';
import { routes } from 'flow/types/routes';
import { LocationType } from 'flow/types/Location';
import { MeetingDTO } from 'flow/types/Meetings';
import { timeFormater } from 'flow/utils/date-time-utils';
import MeetingProviderIcon from './MeetingProviderIcon';
import { isHttpsLink, parseRoomFromLink } from './today-meetings-utils';

type Props = {
    meeting: MeetingDTO;
};

const ROW_CLASS =
    'flex h-12 w-full items-center gap-3 rounded bg-gray-100 px-4 text-start text-sm';
const INTERACTIVE_CLASS = 'hover:darken-10';

/** Times render in the viewer's browser-local zone. */
const formatTimeRange = (start: string, end: string) =>
    `${timeFormater(start)} - ${timeFormater(end)}`;

const TodayMeetingItem = ({ meeting }: Props) => {
    const history = useHistory<LocationType>();
    const { meetingTitle, summary, start, end, meetingType, meetingLink } =
        meeting;

    const room =
        meetingType === 'internal' && meetingLink
            ? parseRoomFromLink(meetingLink)
            : null;
    const externalHref =
        meetingLink && isHttpsLink(meetingLink) ? meetingLink : null;

    const rowInner = (trailingIconKey?: IconKey) => (
        <>
            <MeetingProviderIcon meetingType={meetingType} />
            <span className="flex-1 truncate text-gray-800">
                {meetingTitle ?? summary}
            </span>
            <div className="flex shrink-0 items-center gap-2">
                <span className="text-gray-500">
                    {formatTimeRange(start, end)}
                </span>
                {trailingIconKey && (
                    <Icon
                        iconKey={trailingIconKey}
                        className="text-gray-400"
                        size="1.25rem"
                    />
                )}
            </div>
        </>
    );

    // Internal meetings open inside the app (and inside the desktop app on Electron).
    if (room) {
        return (
            <button
                type="button"
                onClick={() =>
                    history.push(
                        `${routes.videoLink}?room=${encodeURIComponent(room)}`,
                        { scheduledStart: start, scheduledEnd: end },
                    )
                }
                className={twMerge(ROW_CLASS, INTERACTIVE_CLASS)}
            >
                {rowInner('chevron_right')}
            </button>
        );
    }

    // Every other provider with a safe link opens in a new tab.
    if (externalHref) {
        return (
            <a
                href={externalHref}
                target="_blank"
                rel="noopener noreferrer"
                className={twMerge(ROW_CLASS, INTERACTIVE_CLASS)}
            >
                {rowInner('openInNew')}
            </a>
        );
    }

    // No usable link → non-interactive row.
    return <div className={ROW_CLASS}>{rowInner()}</div>;
};

export default TodayMeetingItem;
